<template>
  <div class="pagina">
    <!-- Header -->
    <header class="header">
      <img src="/img/logo-preta.png" height="36" alt="Bike Registrada" />
    </header>

    <!-- Steps -->
    <RegistroBikeStepper :current-step="0" :steps="['Dados pessoais', 'Tipos de registro', 'Finalização']" />

    <!-- Conteúdo -->
    <div class="conteudo">
      <div class="card-form">
        <h2 class="titulo">
          <span class="nome-destaque">{{ nome }}</span>, estamos quase lá
        </h2>
        <p class="subtitulo">Todos os seus dados estão protegidos e não serão compartilhados.</p>

        <div class="campo">
          <label class="campo-label">Qual o seu CPF?</label>
          <p class="campo-dica">Não se preocupe: seu CPF será usado apenas para verificação de identidade e segurança do
            registro.</p>
          <input v-model="cpf" type="text" inputmode="numeric" class="campo-input" :class="{ 'campo-input--erro': cpfInvalido }"
            placeholder="Informe o seu CPF" maxlength="14" autocomplete="off" @input="mascararCPF" />
          <p v-if="cpfInvalido" class="campo-erro">CPF inválido. Verifique e tente novamente.</p>
        </div>

        <p v-if="erroApi" class="campo-erro" style="text-align:center">{{ erroApi }}</p>

        <div class="rodape-form">
          <button class="btn-voltar" @click="voltar" :disabled="carregando">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5017 6.66829H0.835041M0.835041 6.66829L6.66837 0.834961M0.835041 6.66829L6.66837 12.5016"
                stroke="currentColor" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            Voltar
          </button>
          <button class="btn-continuar" :disabled="!podeContinuar || carregando" @click="continuar">
            <span v-if="carregando">Aguarde...</span>
            <template v-else>
              Continuar
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"
                style="transform:scaleX(-1)">
                <path d="M12.5017 6.66829H0.835041M0.835041 6.66829L6.66837 0.834961M0.835041 6.66829L6.66837 12.5016"
                  stroke="currentColor" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </template>
          </button>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <footer class="footer">
      <span>Copyright © 2025 Bike Registrada Online LTDA.</span>
      <div class="footer-contatos">
        <span>Contatos:</span>
        <a href="https://wa.me/556135224521" target="_blank" rel="noopener">
          <i class="fab fa-whatsapp"></i> (61) 3522-4521
        </a>
        <a href="mailto:seguro@bikeregistrada.com.br">
          <i class="far fa-envelope"></i> seguro@bikeregistrada.com.br
        </a>
      </div>
    </footer>
  </div>
</template>

<script>
import axios from "axios";
import config from "@/config";
import auth from "@/auth";
import RegistroBikeStepper from "@/components/RegistroBikeStepper.vue";

function validarCPF(cpf) {
  const nums = cpf.replace(/\D/g, "");
  if (nums.length !== 11 || /^(\d)\1+$/.test(nums)) return false;
  let soma = 0;
  for (let i = 0; i < 9; i++) soma += parseInt(nums[i]) * (10 - i);
  let resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(nums[9])) return false;
  soma = 0;
  for (let i = 0; i < 10; i++) soma += parseInt(nums[i]) * (11 - i);
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  return resto === parseInt(nums[10]);
}

export default {
  name: "DadosCPF",
  components: { RegistroBikeStepper },
  data() {
    return {
      nome: "",
      cpf: "",
      cpfInvalido: false,
      carregando: false,
      erroApi: "",
    };
  },
  computed: {
    podeContinuar() {
      return this.cpf.replace(/\D/g, "").length === 11 && !this.cpfInvalido;
    },
  },
  created() {
    this.nome = sessionStorage.getItem("registro_nome") || "";
    if (!this.nome) {
      this.$router.replace({ name: "registro-bike-dados" });
      return;
    }
    const cpfSalvo = sessionStorage.getItem("registro_cpf") || "";
    if (cpfSalvo) {
      this.cpf = cpfSalvo;
      this.mascararCPF();
    }
  },
  methods: {
    mascararCPF() {
      let v = this.cpf.replace(/\D/g, "");
      if (v.length > 11) v = v.slice(0, 11);
      if (v.length > 9) {
        v = `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9)}`;
      } else if (v.length > 6) {
        v = `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6)}`;
      } else if (v.length > 3) {
        v = `${v.slice(0, 3)}.${v.slice(3)}`;
      }
      this.cpf = v;
      if (v.replace(/\D/g, "").length === 11) {
        this.cpfInvalido = !validarCPF(v);
      } else {
        this.cpfInvalido = false;
      }
    },
    voltar() {
      this.$router.push({ name: "registro-bike-contato" });
    },
    async continuar() {
      if (!this.podeContinuar || this.carregando) return;
      this.erroApi = "";
      this.carregando = true;
      const cpfDigits = this.cpf.replace(/\D/g, "");
      sessionStorage.setItem("registro_cpf", cpfDigits);
      try {
        const response = await axios.post(
          `${config.API_URLV2}/cliente/criar-ou-atualizar`,
          {
            nome: sessionStorage.getItem("registro_nome"),
            email: sessionStorage.getItem("registro_email"),
            cpf: cpfDigits,
            telefone: sessionStorage.getItem("registro_telefone"),
            nascimento: (() => { const d = sessionStorage.getItem("registro_data_nascimento"); return d ? new Date(d.split('/').reverse().join('-')).toISOString() : null; })(),
            uf: sessionStorage.getItem("registro_uf"),
            origem: this.$route.name,
          },
          { headers: { Authorization: undefined } }
        );
        auth.setUserInfo(response.data);
        sessionStorage.setItem("registro_id_cliente", response.data.id);
        this.$router.push({ name: "registro-bike-planos" });
      } catch (e) {
        const erros = e.response && e.response.data && e.response.data.erros;
        this.erroApi = erros && erros.length > 0 ? erros[0] : "Ocorreu um erro ao salvar seus dados. Tente novamente.";
      } finally {
        this.carregando = false;
      }
    },
  },
};
</script>

<style scoped>
.pagina {
  min-height: 100vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background-color: #f9f9f9;
  background-image: url(/img/bike-mundo.svg);
  background-position: right bottom;
  background-repeat: no-repeat;
  background-size: 35%;
  font-family: Inter !important;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* Header */
.header {
  padding: 16px 32px;
  padding-top: calc(16px + env(safe-area-inset-top));
  background: #f9f9f9;
  display: flex;
  align-items: center;
  position: relative;
  z-index: 10;
}

/* Conteúdo */
.conteudo {
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
  position: relative;
  z-index: 10;
}

.card-form {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.08);
  padding: 40px 48px;
  width: 100%;
  max-width: 652px;
}

.titulo {
  font-size: 22px;
  font-weight: 700;
  color: #475569;
  line-height: 1.35;
  margin-bottom: 8px;
}

.nome-destaque {
  color: #4FA935;
}

.subtitulo {
  font-size: 14px;
  color: #64748B;
  margin-bottom: 28px;
  line-height: 1.5;
}

.campo {
  margin-bottom: 24px;
}

.campo-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #64748B;
  margin-bottom: 4px;
}

.campo-dica {
  font-size: 12px;
  color: #94A3B8;
  margin-bottom: 8px;
}

.campo-input {
  width: 100%;
  padding: 12px 14px;
  font-size: 14px;
  border: 1px solid #d0d0d0;
  border-radius: 8px;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  background: #fff;
  color: #3a3a3a;
}

.campo-input:focus {
  border-color: #4FA935;
}

.campo-input::placeholder {
  color: #CBD5E1;
}

.campo-input--erro {
  border-color: #e53935;
}

.campo-erro {
  font-size: 12px;
  color: #e53935;
  margin-top: 6px;
}

.rodape-form {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 48px;
}

.btn-voltar {
  border: none;
  display: flex;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  background: #F0F3F8;
  color: #425994;
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
}

.btn-voltar:hover {
  background: #f0f0f0;
}

.btn-continuar {
  padding: 12px 28px;
  font-size: 15px;
  font-weight: 600;
  background: #4FA935;
  color: #fff;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-continuar:hover:not(:disabled) {
  background: #3d8a28;
}

.btn-continuar:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Footer */
.footer {
  padding: 14px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  color: #64748B;
  position: relative;
  z-index: 10;
}

.footer-contatos {
  display: flex;
  align-items: center;
  gap: 20px;
}

.footer-contatos a {
  color: #4FA935;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 5px;
}

.footer-contatos a:hover {
  color: #4FA935;
}

@media (max-width: 600px) {
  .card-form {
    padding: 28px 20px;
  }

  .pagina {
    background-size: 75%;
  }
  
  .footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 14px 20px;
    font-size: 10px;
    color: #64748B;
    text-align: left;
  }


  .header {
    background: #fff;
    border-bottom: 1px solid #E2E8F0;
  }
}
</style>
